/**
 * Los datos del negocio: quién es, dónde está y cómo se le contacta.
 *
 * Existe porque los documentos legales los piden todos y no había dónde
 * guardarlos: cada plantilla los volvía a pedir entre corchetes, y así se
 * acababan publicando políticas que decían «[NOMBRE DEL NEGOCIO]».
 *
 * Puestos aquí una vez, las plantillas de Políticas llegan ya rellenas con
 * ellos. Por eso la pantalla enlaza allí: los dos sitios son el mismo trabajo
 * visto desde dos lados.
 *
 * Usa el mismo armazón que «Mi cuenta» —`dash-settings-card`, `dash-field`, la
 * cabecera con su icono en pastilla— y no uno propio: las dos son la misma
 * clase de pantalla, un formulario de datos que se rellena una vez, y tener dos
 * maneras de pintar lo mismo se nota al pasar de una a otra.
 *
 * Nada es obligatorio. Casi nadie tiene el RFC a mano el primer día, y una
 * pantalla que no deja guardar hasta tenerlo todo acaba con la gente
 * inventándose datos para poder salir.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBuildingStore,
  IconMapPin,
  IconScale,
} from '@tabler/icons-react'
import { PendingChangesActions } from '../../DashboardTopbarActions'
import { useUnsavedGuard } from '../../../lib/unsavedChanges'
import { useToast } from '../../Toast'
import { getBusinessInfo, saveBusinessInfo, type BusinessInfo } from '../../../lib/api'
import { SectionLoader, useRecordLoader } from '../../RecordLoader'
import { Notice } from '../Notice'
import { StripeAccountRows } from './StripeAccountRows'
import { StripeAttentionBanners } from './StripeAttentionBanners'
import { useI18n } from '../../../lib/i18n'
import { useRegion } from '../../../lib/region'
import { countryLabel } from '../../../lib/displayNames'

type Field = {
  key: keyof BusinessInfo
  /* Claves y no textos: la lista se arma al cargar el módulo, antes de que se
     sepa en qué idioma trabaja quien entra. */
  labelKey: string
  hintKey?: string
  placeholderKey: string
  type?: 'email' | 'tel'
  /**
   * Se enseña pero no se escribe aquí.
   *
   * El país y la moneda los decide «Región y moneda», que es la misma decisión
   * que fija en qué se cobra y qué ley citan las plantillas. Dejarlos también
   * editables aquí daba dos sitios donde escribir lo mismo, y el día que no
   * coincidieran ganaba el que se hubiera guardado el último — con la moneda de
   * los términos diciendo una cosa y la del botón de pagar otra.
   */
  readOnly?: true
  /**
   * Puede quedarse vacío sin que falte nada.
   *
   * Las políticas sueltan la cláusula del teléfono o del RFC si no están, y la
   * razón social cae al nombre comercial. Marcarlos como pendientes con el
   * mismo triángulo que el correo enseñaba a ignorar el aviso entero. La lista
   * de los necesarios vive en el servidor (`REQUIRED_BUSINESS_FIELDS`), que es
   * quien cuenta.
   */
  optional?: true
}

/**
 * Agrupados por para qué sirven, no por dónde acaban.
 *
 * Quien rellena esto está pensando «mis datos» y «mi contacto», no «lo que pide
 * la cláusula 9 de los términos», así que se pregunta en ese orden.
 */
const CARDS: { titleKey: string; noteKey: string; icon: React.ReactNode; fields: Field[] }[] = [
  {
    titleKey: 'business.identity.title',
    noteKey: 'business.identity.note',
    icon: <IconBuildingStore size={18} />,
    fields: [
      { key: 'tradeName', labelKey: 'business.tradeName.label', hintKey: 'business.tradeName.hint', placeholderKey: 'business.tradeName.placeholder' },
      { key: 'legalName', labelKey: 'business.legalName.label', hintKey: 'business.legalName.hint', placeholderKey: 'business.legalName.placeholder', optional: true },
      /* La etiqueta cambia con el país —RFC, NIF, EIN—: ver `TAX_ID_LABEL`. */
      { key: 'taxId', labelKey: 'business.taxId.label', hintKey: 'business.taxId.hint', placeholderKey: 'business.taxId.placeholder', optional: true },
    ],
  },
  {
    titleKey: 'business.address.title',
    noteKey: 'business.address.note',
    icon: <IconMapPin size={18} />,
    fields: [
      { key: 'address', labelKey: 'business.address.label', hintKey: 'business.address.hint', placeholderKey: 'business.address.placeholder' },
      { key: 'city', labelKey: 'business.city.label', placeholderKey: 'business.city.placeholder' },
      { key: 'state', labelKey: 'business.state.label', placeholderKey: 'business.state.placeholder' },
      { key: 'country', labelKey: 'business.country.label', placeholderKey: 'business.country.placeholder', readOnly: true },
      { key: 'currency', labelKey: 'business.currency.label', placeholderKey: 'business.currency.placeholder', readOnly: true },
    ],
  },
  {
    titleKey: 'business.contact.title',
    noteKey: 'business.contact.note',
    icon: <IconScale size={18} />,
    fields: [
      { key: 'email', labelKey: 'business.email.label', hintKey: 'business.email.hint', placeholderKey: 'business.email.placeholder', type: 'email' },
      { key: 'phone', labelKey: 'business.phone.label', hintKey: 'business.phone.hint', placeholderKey: 'business.phone.placeholder', type: 'tel', optional: true },
      { key: 'legalContact', labelKey: 'business.legalContact.label', hintKey: 'business.legalContact.hint', placeholderKey: 'business.legalContact.placeholder', optional: true },
    ],
  },
]

/**
 * Cómo se llama la identificación fiscal donde opera el negocio.
 *
 * Decía «RFC» en cualquier país, y a quien opera en España o en Estados Unidos
 * le pedía un dato que no tiene. Sale del mismo perfil legal que eligen las
 * plantillas, así que el formulario y el documento lo llaman igual.
 */
const TAX_ID_LABEL: Record<string, string> = {
  mx: 'business.taxId.label.mx',
  es: 'business.taxId.label.es',
  us: 'business.taxId.label.us',
  generic: 'business.taxId.label',
}

const EMPTY: BusinessInfo = {
  tradeName: '', legalName: '', taxId: '', address: '', city: '', state: '',
  country: '', email: '', phone: '', legalContact: '', currency: '',
}

export function BusinessInfoSection({ projectId, token, onBack, onOpenPolicies, onOpenRegion }: {
  projectId: string
  token: string
  /** Vuelta al índice de Configuración, de donde se abre esta pantalla. */
  onBack?: (() => void) | undefined
  onOpenPolicies?: (() => void) | undefined
  /** A donde se cambian el país y la moneda, que aquí sólo se enseñan. */
  onOpenRegion?: (() => void) | undefined
}) {
  const { showToast } = useToast()
  const { t, language } = useI18n()
  const region = useRegion()
  const [saved, setSaved] = useState<BusinessInfo>(EMPTY)
  const [draft, setDraft] = useState<BusinessInfo>(EMPTY)
  const [progress, setProgress] = useState({ filled: 0, total: 0 })
  const [loading, setLoading] = useState(true)
  /* Sigue visible mientras se despide, aunque los datos ya hayan llegado. */
  const businessLoader = useRecordLoader(loading)
  const [failed, setFailed] = useState('')
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { filled, total, ...info } = await getBusinessInfo(token, projectId)
      setSaved(info)
      setDraft(info)
      setProgress({ filled, total })
      setFailed('')
    } catch (error) {
      setFailed(error instanceof Error ? error.message : t('business.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [projectId, token, t])

  useEffect(() => { void load() }, [load])

  const dirty = (Object.keys(EMPTY) as (keyof BusinessInfo)[]).some((key) => draft[key] !== saved[key])

  const save = async () => {
    setSaving(true)
    try {
      const { filled, total, ...info } = await saveBusinessInfo(token, projectId, draft)
      setSaved(info)
      setDraft(info)
      setProgress({ filled, total })
      showToast(t('business.saved'))
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('business.saveFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  /* Volver con datos a medio escribir hace parpadear la barra de arriba en vez
     de perderlos. */
  const blockUnsaved = useUnsavedGuard()

  const missing = progress.total - progress.filled

  /* La espera sustituye a la pantalla ENTERA, título y descripción incluidos:
     estando dentro de la tarjeta, la cabecera se pintaba sobre un hueco y luego
     se recolocaba al llegar el contenido. Ver `SectionLoader`. */
  if (businessLoader.visible) return <SectionLoader loader={businessLoader} label={t('business.loading')} />

  return (
    <div className="dash-dashboard-view dash-settings-view dash-business-settings dash-loaded-content-in">
      <div className="dash-settings-layout">
        <header className="dash-settings-intro">
          {onBack ? (
            <button type="button" className="dash-secondary-btn dash-settings-back" onClick={() => { if (!blockUnsaved()) onBack() }}>
              <IconArrowLeft size={14} /> {t('config.back')}
            </button>
          ) : null}
          <div className="dash-header" style={{ margin: 0 }}>
            <h1>{t('business.title')}</h1>
            <p>
              {t('business.introBefore')}{' '}
              {onOpenPolicies ? (
                <button type="button" className="dash-inline-link" onClick={onOpenPolicies}>{t('business.policiesTemplates')}</button>
              ) : t('business.policiesTemplates')}{' '}
              {t('business.introAfter')}
            </p>
          </div>
          <PendingChangesActions
            dirty={dirty}
            saving={saving}
            onDiscard={() => setDraft(saved)}
            onSave={() => void save()}
          />
        </header>

        <div className="dash-settings-content">
          {failed ? (
            <div className="dash-placeholder dash-placeholder--inset">
              <strong>{t('business.loadFailedTitle')}</strong>
              <span>{failed}</span>
              <button type="button" className="dash-secondary-btn" onClick={() => void load()}>{t('business.retry')}</button>
            </div>
          ) : (
            <div className="dash-account-grid">
              <StripeAttentionBanners projectId={projectId} token={token} />
              {missing > 0 ? (
                <Notice title={missing === 1 ? t('business.missingOne') : t('business.missingMany', { count: missing })}>
                  <p>{t('business.missingBody')}</p>
                </Notice>
              ) : null}

              {CARDS.map((card) => (
                <section className="dash-settings-card is-raised" key={card.titleKey}>
                  <div className="dash-settings-card-head">
                    <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">{card.icon}</span>
                    <div className="dash-settings-card-head-copy">
                      <h3>{t(card.titleKey)}</h3>
                      <p>{t(card.noteKey)}</p>
                    </div>
                  </div>

                  {card.fields.map((field) => {
                    const fieldMissing = !field.optional && !field.readOnly && draft[field.key].trim() === ''
                    const label = field.key === 'taxId' ? t(TAX_ID_LABEL[region.profile] ?? field.labelKey) : t(field.labelKey)
                    return (
                      <label className="dash-field" key={field.key}>
                        <span className="dash-business-field-label">
                          <span>{label}{field.optional ? <span className="dash-field-optional"> · {t('business.optional')}</span> : null}</span>
                          {fieldMissing ? (
                            <span className="dash-business-field-warning" title={t('business.fieldMissing')}>
                              <IconAlertTriangle size={13} aria-hidden="true" />
                              <span className="dash-sr-only">{t('business.fieldMissing')}</span>
                            </span>
                          ) : null}
                        </span>
                        <input
                          type={field.type ?? 'text'}
                          /* El país llega escrito en castellano desde el
                             servidor; aquí se enseña en el idioma del panel. */
                          value={field.key === 'country' && draft.country
                            ? countryLabel(region.countryCode, language, draft.country)
                            : draft[field.key]}
                          placeholder={t(field.placeholderKey)}
                          {...(field.readOnly
                            ? { readOnly: true, 'aria-readonly': true as const }
                            : { onChange: (event: React.ChangeEvent<HTMLInputElement>) => setDraft((current) => ({ ...current, [field.key]: event.target.value })) })}
                        />
                        {field.readOnly ? (
                          <p className="dash-field-hint">
                            {t('business.changeIn')}{' '}
                            {onOpenRegion
                              ? <button type="button" className="dash-inline-link" onClick={onOpenRegion}>{t('section.region')}</button>
                              : t('business.regionPath')}.
                          </p>
                        ) : field.hintKey ? <p className="dash-field-hint">{t(field.hintKey)}</p> : null}
                      </label>
                    )
                  })}
                </section>
              ))}

              {/* Al final: lo que de tu negocio guarda Stripe. Ver
                  `StripeAccountRows`. */}
              <StripeAccountRows projectId={projectId} token={token} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
